/* Luxuntique 2026 — "Ask in your own words" in the search pop-up (snippets/lux-search-ask.liquid).
   - Tapping an example puts it in the search box and runs the theme's own predictive search.
   - While the box is empty and the pop-up is open, the placeholder types the examples out in turn.
     Reduced motion: a still placeholder instead. */

const TYPE_MS = 55;
const ERASE_MS = 25;
const HOLD_MS = 1800;
const GAP_MS = 400;

class LuxSearchAsk extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {number} */
  #timer = 0;
  #running = false;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const input = this.#input();
    if (!input) return;

    const still = this.dataset.placeholder || input.placeholder;
    input.placeholder = still;

    this.addEventListener(
      'click',
      (event) => {
        const chip = event.target instanceof Element ? event.target.closest('[data-ask]') : null;
        if (!(chip instanceof HTMLElement)) return;
        event.preventDefault();
        this.#stop();
        input.value = chip.dataset.ask || '';
        input.focus();
        // The theme's search only reacts to typing that says what was typed (inputType)
        input.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertText', data: input.value }));
      },
      { signal }
    );

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Type only while the pop-up is open and the box is empty
    const dialog = this.closest('dialog');
    const update = () => {
      const open = dialog ? dialog.open : true;
      if (open && input.value === '') this.#start();
      else this.#stop();
    };
    input.addEventListener('input', update, { signal });
    input.addEventListener('blur', update, { signal });
    if (dialog) new MutationObserver(update).observe(dialog, { attributes: true, attributeFilter: ['open'] });
    signal.addEventListener('abort', () => this.#stop());
    update();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /** @returns {HTMLInputElement | null} */
  #input() {
    const input = this.closest('form')?.querySelector('input.search-input');
    return input instanceof HTMLInputElement ? input : null;
  }

  #examples() {
    return [...this.querySelectorAll('[data-ask]')].map((chip) => /** @type {HTMLElement} */ (chip).dataset.ask || '');
  }

  #start() {
    if (this.#running) return;
    const input = this.#input();
    const examples = this.#examples().filter(Boolean);
    if (!input || !examples.length) return;
    this.#running = true;

    const prefix = this.dataset.try ? `${this.dataset.try} ` : '';
    let index = 0;
    let length = 0;
    let erasing = false;

    const step = () => {
      if (!this.#running) return;
      const text = `“${examples[index]}”`;
      if (!erasing) {
        length += 1;
        input.placeholder = prefix + text.slice(0, length);
        if (length >= text.length) {
          erasing = true;
          this.#timer = window.setTimeout(step, HOLD_MS);
          return;
        }
        this.#timer = window.setTimeout(step, TYPE_MS);
      } else {
        length -= 1;
        input.placeholder = prefix + text.slice(0, Math.max(length, 0));
        if (length <= 0) {
          erasing = false;
          index = (index + 1) % examples.length;
          this.#timer = window.setTimeout(step, GAP_MS);
          return;
        }
        this.#timer = window.setTimeout(step, ERASE_MS);
      }
    };
    this.#timer = window.setTimeout(step, 600);
  }

  #stop() {
    this.#running = false;
    window.clearTimeout(this.#timer);
    const input = this.#input();
    if (input && this.dataset.placeholder) input.placeholder = this.dataset.placeholder;
  }
}

if (!customElements.get('lux-search-ask')) customElements.define('lux-search-ask', LuxSearchAsk);
